import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Image, Input, Select, Space, message } from 'antd';
import { PictureOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import MediaLibrary, { getMediaStorageUrl } from '../../components/MediaLibrary/MediaLibrary';

const { TextArea } = Input;
const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL || 'http://localhost:3003';

const MediaField = ({ label, selectedMedia, onOpenLibrary, onClear }) => (
  <Form.Item label={label}>
    <div>
      {selectedMedia ? (
        <div style={{ marginBottom: 8 }}>
          <Image
            src={selectedMedia.url}
            alt={selectedMedia.originalFilename}
            style={{ maxWidth: '100%', maxHeight: 140, objectFit: 'cover' }}
          />
          <div style={{ marginTop: 4, fontSize: 12, color: '#666' }}>
            {selectedMedia.originalFilename}
          </div>
        </div>
      ) : null}
      <Space>
        <Button icon={<PictureOutlined />} onClick={onOpenLibrary}>
          {selectedMedia ? 'Changer l\'image' : 'Sélectionner une image'}
        </Button>
        {selectedMedia && (
          <Button onClick={onClear}>Supprimer</Button>
        )}
      </Space>
    </div>
  </Form.Item>
);

const ElseworldForm = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [universes, setUniverses] = useState([]);
  const [elseworld, setElseworld] = useState(null);
  const [bannerMedia, setBannerMedia] = useState(null);
  const [logoMedia, setLogoMedia] = useState(null);
  const [mediaLibraryOpen, setMediaLibraryOpen] = useState(false);
  const [activeMediaField, setActiveMediaField] = useState(null);

  useEffect(() => {
    fetchUniverses();
    if (isEditing) {
      fetchElseworld();
    }
  }, [id, isEditing]);

  const fetchUniverses = async () => {
    try {
      const response = await api.get('/api/admin/universes');
      setUniverses(response.data?.universes || []);
    } catch (error) {
      message.error(error.response?.data?.error || 'Erreur lors du chargement des univers');
    }
  };

  const fetchElseworld = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/api/admin/elseworlds/${id}`);
      const data = response.data;
      setElseworld(data);
      form.setFieldsValue({
        name: data.name,
        slug: data.slug,
        description: data.description || '',
        parent_universe_id: data.parentUniverse?.id,
      });

      if (data.banner) {
        setBannerMedia({ url: data.banner, originalFilename: 'Bannière' });
      }
      if (data.logo) {
        setLogoMedia({ url: data.logo, originalFilename: 'Logo' });
      }
    } catch (error) {
      message.error(error.response?.data?.error || 'Erreur lors du chargement de l\'elseworld');
      navigate('/elseworlds');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = {
        name: values.name,
        slug: values.slug,
        description: values.description || null,
        parent_universe_id: values.parent_universe_id,
        banner: getMediaStorageUrl(bannerMedia) || null,
        logo: getMediaStorageUrl(logoMedia) || null,
      };

      if (isEditing) {
        await api.put(`/api/admin/elseworlds/${id}`, payload);
        message.success('Elseworld mis à jour avec succès');
      } else {
        await api.post('/api/admin/elseworlds', payload);
        message.success('Elseworld créé avec succès');
      }
      navigate('/elseworlds');
    } catch (error) {
      message.error(error.response?.data?.error || 'Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  const openMediaLibrary = (field) => {
    setActiveMediaField(field);
    setMediaLibraryOpen(true);
  };

  const handleMediaSelect = (media) => {
    if (activeMediaField === 'banner') {
      setBannerMedia(media);
    } else if (activeMediaField === 'logo') {
      setLogoMedia(media);
    }
    setMediaLibraryOpen(false);
  };

  const activeMediaValue = activeMediaField === 'banner' ? bannerMedia : logoMedia;
  const parentUniverse = universes.find(
    (universe) => universe.id === form.getFieldValue('parent_universe_id'),
  ) || elseworld?.parentUniverse;
  const publicUrl = parentUniverse?.slug && (form.getFieldValue('slug') || elseworld?.slug)
    ? `${FRONTEND_URL}/univers/${parentUniverse.slug}/elseworld/${form.getFieldValue('slug') || elseworld.slug}`
    : null;

  return (
    <div>
      <Card
        title={isEditing ? `Modifier l'elseworld${elseworld?.name ? ` : ${elseworld.name}` : ''}` : 'Créer un elseworld'}
        loading={loading && isEditing && !elseworld}
      >
        {publicUrl && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Page publique"
            description={(
              <a href={publicUrl} target="_blank" rel="noopener noreferrer">
                {publicUrl}
              </a>
            )}
          />
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="Nom"
            rules={[{ required: true, message: 'Le nom est requis' }]}
          >
            <Input placeholder="Kingdom Come" />
          </Form.Item>

          <Form.Item
            name="slug"
            label="Slug"
            rules={[{ required: true, message: 'Le slug est requis' }]}
          >
            <Input placeholder="kingdom-come" />
          </Form.Item>

          <Form.Item
            name="parent_universe_id"
            label="Univers parent"
            rules={[{ required: true, message: 'L\'univers parent est requis' }]}
          >
            <Select
              showSearch
              placeholder="Sélectionnez un univers parent"
              optionFilterProp="children"
            >
              {universes.map((universe) => (
                <Select.Option key={universe.id} value={universe.id}>
                  {universe.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="description" label="Description">
            <TextArea rows={4} placeholder="Description de cet elseworld" />
          </Form.Item>

          <MediaField
            label="Bannière"
            selectedMedia={bannerMedia}
            onOpenLibrary={() => openMediaLibrary('banner')}
            onClear={() => setBannerMedia(null)}
          />

          <MediaField
            label="Logo"
            selectedMedia={logoMedia}
            onOpenLibrary={() => openMediaLibrary('logo')}
            onClear={() => setLogoMedia(null)}
          />

          <Form.Item style={{ marginTop: 24 }}>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                {isEditing ? 'Enregistrer' : 'Créer'}
              </Button>
              <Button onClick={() => navigate('/elseworlds')}>
                Annuler
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <MediaLibrary
        open={mediaLibraryOpen}
        onClose={() => setMediaLibraryOpen(false)}
        onSelect={handleMediaSelect}
        value={activeMediaValue}
      />
    </div>
  );
};

export default ElseworldForm;
